import { matchRoutes, type RouteObject } from 'react-router-dom';
import { loadWarmupModule } from 'utils/chunkReload';
// The registry file rather than its index, which also exports
// useCandidateNavigate and would bring the router hooks into the boot chunk.
import {
  candidateMountPath,
  candidateRouteKeys,
  type CandidateRouteKey,
} from 'candidate-url-registry/registry';

import {
  loadAlternativeRole,
  loadWebCall,
  loadFeedback,
  loadFeedbackSuccess,
  loadHMIntake,
  loadInterviewCompleted,
  loadInterviewRejoin,
  loadInterviewScheduling,
  loadOptOutSuccess,
  loadPracticeCompletion,
  loadPreapplyOptOut,
  loadRescheduleInterview,
  loadRescheduleReason,
  loadResumeFeedbackDetails,
  loadReviewAction,
  loadScreenSharePermissionHelp,
  loadUninterested,
  loadVideoPermissionHelp,
  loadWebCallError,
  loadWithdrawal,
} from './routeChunks';

export type CandidateRouteChunk = () => Promise<unknown>;

/**
 * The chunk of the page each lazily mounted route renders (routeMounts.tsx,
 * which a test holds this to).
 */
export const candidateRouteChunks: Partial<
  Record<CandidateRouteKey, CandidateRouteChunk>
> = {
  WEB_INTERVIEW_SESSION: loadWebCall,
  WEB_INTERVIEW_TEST_CALL: loadWebCall,
  WEB_INTERVIEW_PRACTICE: loadWebCall,
  WEB_INTERVIEW: loadWebCall,
  WEB_CALL_LEGACY: loadWebCall,
  WEB_INTERVIEW_SESSION_REJOIN: loadInterviewRejoin,
  WEB_INTERVIEW_SESSION_SCHEDULE: loadInterviewScheduling,
  WEB_INTERVIEW_SESSION_SCHEDULE_CONFIRM: loadInterviewScheduling,
  WEB_INTERVIEW_SESSION_SCHEDULE_SCHEDULED: loadInterviewScheduling,
  FEEDBACK_SESSION: loadFeedback,
  FEEDBACK_SESSION_SUCCESS: loadFeedbackSuccess,
  WEB_INTERVIEW_TEST_CALL_REJOIN: loadInterviewRejoin,
  WEB_INTERVIEW_REJOIN: loadInterviewRejoin,
  WEB_INTERVIEW_SCHEDULE: loadInterviewScheduling,
  WEB_INTERVIEW_SCHEDULE_CONFIRM: loadInterviewScheduling,
  WEB_INTERVIEW_SCHEDULE_SCHEDULED: loadInterviewScheduling,
  WEB_CALL_LEGACY_SCHEDULE: loadInterviewScheduling,
  WEB_CALL_LEGACY_SCHEDULE_CONFIRM: loadInterviewScheduling,
  WEB_CALL_LEGACY_SCHEDULE_SCHEDULED: loadInterviewScheduling,
  WEB_CALL_LEGACY_FEEDBACK: loadFeedback,
  RESCHEDULE: loadRescheduleReason,
  RESCHEDULE_CALENDAR: loadRescheduleInterview,
  WEB_INTERVIEW_ERROR: loadWebCallError,
  WEB_INTERVIEW_COMPLETED: loadInterviewCompleted,
  WEB_INTERVIEW_VIDEO_HELP: loadVideoPermissionHelp,
  WEB_INTERVIEW_SCREEN_SHARE_HELP: loadScreenSharePermissionHelp,
  FEEDBACK_SUCCESS: loadFeedbackSuccess,
  FEEDBACK_TEST_CALL_SUCCESS: loadFeedbackSuccess,
  FEEDBACK: loadFeedback,
  FEEDBACK_TEST_CALL: loadFeedback,
  OPT_OUT_SUCCESS: loadOptOutSuccess,
  ACCOMMODATION_REQUEST_SUCCESS: loadOptOutSuccess,
  PRACTICE_COMPLETED: loadPracticeCompletion,
  REJECTION_REVIEW: loadReviewAction,
  CANDIDATE_RESUME_FEEDBACK_DETAILS: loadResumeFeedbackDetails,
  CANDIDATE_WITHDRAW: loadWithdrawal,
  CANDIDATE_UNINTERESTED: loadUninterested,
  CANDIDATE_ALTERNATIVE_ROLE: loadAlternativeRole,
  CANDIDATE_PREAPPLY_OPT_OUT: loadPreapplyOptOut,
  HM_INTAKE: loadHMIntake,
};

/** A candidate route the router renders at a pathname, with its params. */
export interface CandidateRouteMatch {
  key: CandidateRouteKey;
  params: Readonly<Record<string, string | undefined>>;
}

type MatchRoute = RouteObject & { key: CandidateRouteKey };

/**
 * Pure: the route the candidate router renders at `pathname`, or null.
 * matchRoutes ranks the mounts the way the router does, so
 * /web-interview/session resolves to the session route rather than to
 * /web-interview/:encodedId.
 */
export function matchCandidateRoute(
  pathname: string
): CandidateRouteMatch | null {
  const routes: MatchRoute[] = candidateRouteKeys.map((key) => ({
    path: candidateMountPath(key),
    key,
  }));
  const matches = matchRoutes(routes, pathname);
  const match = matches?.[matches.length - 1];
  return match ? { key: match.route.key, params: match.params } : null;
}

/**
 * Starts downloading the chunk of the lazy page the router will render at
 * `pathname`, so it overlaps the app's own download instead of following it,
 * and returns the route it matched.
 *
 * A failed fetch (a stale build's chunk after a deploy) neither reloads the
 * page nor surfaces: the router imports the same chunk through
 * lazyWithReload, which owns recovery.
 */
export function preloadCandidateRouteChunk(
  pathname: string
): CandidateRouteMatch | null {
  const route = matchCandidateRoute(pathname);
  const load = route ? candidateRouteChunks[route.key] : undefined;
  if (load) warmCandidateRouteChunk(load);
  return route;
}

/**
 * Starts downloading a page chunk the candidate is likely to open next. A
 * warm-up whose chunk fails to load must not reload the page under the
 * candidate (they may have typed into it), so it runs as a warm-up import.
 */
export function warmCandidateRouteChunk(load: CandidateRouteChunk): void {
  // swallow-ok: only the warm-up failed; the router's lazyWithReload imports the chunk again and owns the retry
  void loadWarmupModule(load).catch(() => {});
}
